import {type Callback, type CleanupJobs, Dispatchable, type RemoveHandle} from "../base/Dispatcher";
import {isString, toArray} from "../base/Utils";
// Store imports this module back, so the reference has to stay erasable
import {type StoreObject} from "./Store";

export type StoreId = string | number;
export type StoreIdOrNull = StoreId | null | undefined;

export interface StoreEvent {
    // Absent on an event built locally, where the applyX method being called is the type
    type?: string;
    objectId?: StoreId;
    // The object itself on a create or update, and whatever the publisher sent on any other event
    data?: RawStoreObject | StoreObject;
    isFake?: boolean;
    [key: string]: unknown;
}

export interface StateEvent extends StoreEvent {
    objectType?: string;
    state?: RawStateData; // events may have an extra state that is applied before the object
}

declare global {
    // ts-plugin/ adds an entry for every @globalStore class, keyed by the name its store was declared with,
    // so looking a store up by that name says what it holds. Empty without the plugin, which only costs
    // precision - an unlisted name still resolves through the general signature.
    interface StemStoreRegistry {}
}

// Every name a store is registered under, and so every name a lookup by name can find
export type StoreObjectType = keyof StemStoreRegistry;

// What filterBy matches against. Only the names are checked: a value may be a list, any member of which
// matches, and the comparison itself is loose, so a string id stands for the numeric field it names
export type StoreFilter<T> = {[Key in keyof T]?: unknown};

// getStore() hands back the store class itself, so its statics are callable on the result. Each one below
// mirrors what StoreObject declares, which is where the shapes are decided.
export interface StoreInterface<BaseType extends StoreObject = StoreObject> {
    objectType: StoreObjectType;
    dependencies: StoreObjectType[];
    getState(): State;
    applyEvent(event: StateEvent): BaseType | undefined;
    get(id: StoreId): BaseType | undefined;
    getRequired(id: StoreId): BaseType;
    importState(objects: RawStoreObject[]): void;
    clear?(): void;
    toJSON(): RawStoreObject[];
    getObjects(): Map<string, BaseType>;
    all(): BaseType[];
    filterBy(filter: StoreFilter<BaseType>): BaseType[];
    findBy(filter: StoreFilter<BaseType>): BaseType | undefined;
    // Every store is a Dispatchable
    addChangeListener(callback: Callback): RemoveHandle | CleanupJobs | undefined;
}

// The form a store object arrives in and goes back out as, before any field is read
export interface RawStoreObject {
    id?: StoreId;
    [key: string]: unknown;
}

export type RawStateData = Record<string, RawStoreObject[]>;

export interface StateLoadOptions {
    state?: RawStateData;
    events?: StateEvent | StateEvent[];
}

export type StateData = RawStateData | StateLoadOptions;

export class State extends Dispatchable {
    stores = new Map<string, StoreInterface>();

    getStore<Name extends keyof StemStoreRegistry>(objectType: Name): StoreInterface<StemStoreRegistry[Name]> | undefined;
    getStore(objectType: string | { objectType?: string } | null | undefined): StoreInterface | undefined;
    getStore(objectType: string | { objectType?: string } | null | undefined): StoreInterface | undefined {
        const objectName = isString(objectType) ? objectType?.toLowerCase() : objectType?.objectType;
        return this.stores.get(objectName);
    }

    getStoreForEvent(event: StateEvent): StoreInterface | undefined {
        const objectType = event.objectType;
        return this.getStore(objectType);
    }

    addStore(store: StoreInterface): void {
        const objectType = store.objectType;
        if (!this.stores.has(objectType)) {
            this.stores.set(objectType, store);
        } else {
            throw new Error("GlobalState: Adding a store for an existing object type: " + store.objectType);
        }
    }

    applyEvent(event: StateEvent | StateEvent[] | null | undefined): void {
        if (event == null) {
            return;
        }
        if (Array.isArray(event)) {
            for (const individualEvent of event) {
                this.applyEvent(individualEvent);
            }
            return;
        }
        if (event.state) {
            this.importState(event.state);
            // We can have events that only have a state
            if (!this.getStoreForEvent(event)) {
                return;
            }
        }
        const store = this.getStoreForEvent(event);
        if (store) {
            store.applyEvent(event);
        } else {
            console.log("GlobalState: Missing store for event: ", event);
        }
    }

    // Bound to the instance, so it can be handed straight to an event stream as a listener
    applyEventWrapper = (event: StateEvent | StateEvent[] | null | undefined): void => this.applyEvent(event);

    registerStream(streamName: string): void {
        console.error("Websockets are not enabled, can't register stream:", streamName);
    }

    // Import the store for objectType and remove it from stateMap
    importStateFromTempMap(objectType: string, stateMap: Map<string, RawStoreObject[]>): void {
        const storeState = stateMap.get(objectType);
        stateMap.delete(objectType);
        if (storeState == null) {
            // Probably a dependency that isn't in the state
            return;
        }

        const store = this.getStore(objectType);

        if (!store) {
            console.error("Failed to import state, can't find store ", objectType);
            return;
        }
        for (const dependency of store.dependencies) {
            this.importStateFromTempMap(dependency.toLowerCase(), stateMap);
        }
        store.importState(storeState);
    }

    // Imports the state information from a plain object
    importState(state: StateData | StateData[]): void {
        if (Array.isArray(state)) {
            for (const obj of state) {
                this.importState(obj);
            }
            return;
        }
        if (state.state || state.events) {
            // Must be a recursive object
            // TODO Technically not correct since we need to respect disableState/Event import for the request itself
            this.load(state);
            return;
        }
        // Import everything in a map and then do an implicit topological sort by dependencies
        const stateMap = new Map<string, RawStoreObject[]>();
        for (const [objectType, objects] of Object.entries(state)) {
            stateMap.set(objectType.toLowerCase(), objects);
        }
        while (stateMap.size > 0) {
            const allKeys = stateMap.keys();
            const objectType = allKeys.next().value!; // size > 0 guarantees a remaining key
            this.importStateFromTempMap(objectType, stateMap);
        }
    }

    // Loads both the state and the events
    load({state, events}: StateLoadOptions, disableStateImport?: boolean, disableEventsImport?: boolean): void {
        if (state && !disableStateImport) {
            this.importState(state);
        }
        if (events && !disableEventsImport) {
            this.applyEvent(events);
        }
    }

    clear(): void {
        for (const store of this.stores.values()) {
            store.clear && store.clear();
        }
    }

    toJSON(): RawStateData {
        const state: RawStateData = {};
        for (const store of this.stores.values()) {
            // A singleton store is its own object, so it answers with one rather than a list of them
            state[store.objectType] = toArray(store.toJSON());
        }
        return state;
    }
}

// When creating a store without an explicit state, this value should be assumed
export const GlobalState = new State();
